import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const INSTRUMENTS = [
  "Guitar", "Bass", "Piano", "Violin", "Harp", "Handpan",
  "Djembe", "Tabla", "Dhol", "Rubab", "Trumpet", "Trombone",
  "Saxophone", "Drumset", "Snare Drum", "Keys", "Shehnai",
  "Bansuri", "Dilruba", "Tumbi", "Sitar", "Flute", "Cello", "Other"
];

const REGIONS = ["East", "West", "Central"];

const GEAR_ITEMS = [
  "Instrument (primary)",
  "Amplifier",
  "DI Box",
  "Instrument Cables",
  "XLR Cables",
  "Microphone",
  "Mic Stand",
  "Music Stand",
  "In-Ear Monitors",
  "Pedal Board / Effects",
  "Drum Hardware",
  "Drum Throne",
  "Cymbal Bag",
  "Keyboard Stand",
  "Power Strip / Extension Cord",
  "Backup Strings / Reeds",
];

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().min(7, "Phone is required").max(20),
  instagram_handle: z.string().trim().min(1, "Instagram handle is required").max(100),
  music_profile_url: z.string().trim().url("Enter a valid URL").max(500).or(z.literal("")).optional().default(""),
  primary_instrument: z.string().min(1, "Select your primary instrument"),
  region: z.string().min(1, "Select your region"),
  mailing_address: z.string().trim().min(1, "Mailing address is required").max(500),
  availability_weekdays: z.boolean().default(false),
  availability_weekends: z.boolean().default(false),
  travel_radius_miles: z.number({ required_error: "Travel radius is required" }).min(1, "Enter a valid travel radius").max(5000),
  minimum_notice: z.string().min(1, "Minimum notice is required"),
  desired_hourly_rate: z.number().min(0, "Enter a valid rate").max(10000),
  owns_equipment: z.enum(["yes", "no"], { required_error: "Select an option" }),
  gear_owned: z.array(z.string()).default([]),
  gear_notes: z.string().trim().max(500).optional().default(""),
  venue_needs: z.array(z.string()).min(1, "Select at least one option"),
  tech_rider_notes: z.string().trim().max(500).optional().default(""),
}).refine(data => data.availability_weekdays || data.availability_weekends, {
  message: "Select at least one availability option",
  path: ["availability_weekdays"],
});

export type AvailabilityFormData = z.infer<typeof schema>;

interface AvailabilityGateProps {
  onComplete: (data: AvailabilityFormData) => void;
}

interface AddressSuggestion {
  place_name: string;
  center: [number, number];
}

export const AvailabilityGate = ({ onComplete }: AvailabilityGateProps) => {
  const form = useForm<AvailabilityFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      instagram_handle: "",
      music_profile_url: "",
      primary_instrument: "",
      region: "",
      mailing_address: "",
      availability_weekdays: false,
      availability_weekends: false,
      travel_radius_miles: undefined as any,
      minimum_notice: "",
      desired_hourly_rate: undefined as any,
      owns_equipment: undefined as any,
      gear_owned: [],
      gear_notes: "",
      venue_needs: [],
      tech_rider_notes: "",
    },
  });

  const [addressInput, setAddressInput] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const handleAddressInput = (value: string) => {
    setAddressInput(value);
    setIsVerified(false);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (value.length > 2) {
      debounceTimer.current = setTimeout(() => {
        const token = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;
        if (!token) return;
        fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(value)}.json?access_token=${token}&types=address,place,locality&country=us&limit=5&autocomplete=true`
        )
          .then(res => res.json())
          .then(data => {
            if (data.features) {
              setSuggestions(data.features);
              setShowSuggestions(true);
            }
          })
          .catch(() => {});
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    const address = suggestion.place_name;
    setAddressInput(address);
    setIsVerified(true);
    setShowSuggestions(false);
    setSuggestions([]);
    form.setValue("mailing_address", address, { shouldValidate: true });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground text-sm">
          These questions help us understand your professional availability and reliability.
          <br />
          <span className="font-medium">This is not a music test.</span>
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onComplete)} className="space-y-5">
          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name *</FormLabel>
                <FormControl><Input placeholder="Your name" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="email" render={({ field }) => (
              <FormItem>
                <FormLabel>Email *</FormLabel>
                <FormControl><Input type="email" placeholder="your@email.com" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="phone" render={({ field }) => (
              <FormItem>
                <FormLabel>Phone *</FormLabel>
                <FormControl><Input placeholder="+1 555-0123" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          {/* Social & Music Profiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={form.control} name="instagram_handle" render={({ field }) => (
              <FormItem>
                <FormLabel>Instagram Handle *</FormLabel>
                <FormControl><Input placeholder="@yourhandle" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="music_profile_url" render={({ field }) => (
              <FormItem>
                <FormLabel>Spotify / SoundCloud / YouTube Link</FormLabel>
                <FormControl><Input placeholder="https://open.spotify.com/artist/..." {...field} /></FormControl>
                <p className="text-xs text-muted-foreground mt-1">Your artist page on any platform</p>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          {/* Mailing Address with Mapbox autocomplete */}
          <FormField control={form.control} name="mailing_address" render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2 mb-1">
                <FormLabel>Mailing Address *</FormLabel>
                {isVerified && (
                  <Badge 
                    variant="secondary" 
                    className="gap-1 py-0.5 px-2 text-[10px] font-bold bg-gradient-to-r from-green-500/25 to-green-500/25 text-green-600 dark:text-green-400 border border-green-500/60 shadow-sm rounded"
                  >
                    <Check className="h-2.5 w-2.5 stroke-[3]" />
                    <span>Verified</span>
                  </Badge>
                )}
              </div>
              <div className="relative">
                <FormControl>
                  <Input
                    placeholder="Search your mailing address..."
                    value={addressInput}
                    onChange={(e) => {
                      handleAddressInput(e.target.value);
                      field.onChange(e.target.value);
                    }}
                  />
                </FormControl>
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.place_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )} />

          {/* Instrument & Region */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={form.control} name="primary_instrument" render={({ field }) => (
              <FormItem>
                <FormLabel>Primary Instrument *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select instrument" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {INSTRUMENTS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="region" render={({ field }) => (
              <FormItem>
                <FormLabel>Region (USA) *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          {/* Desired Pay */}
          <FormField control={form.control} name="desired_hourly_rate" render={({ field }) => (
            <FormItem>
              <FormLabel>Desired Pay Per Hour (USD) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  max={10000}
                  placeholder="e.g. 150"
                  {...field}
                  value={field.value ?? ""}
                  onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />

          {/* Availability */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <p className="text-sm font-medium">Typical Availability *</p>
              <FormField control={form.control} name="availability_weekdays" render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Weekdays</FormLabel>
                </FormItem>
              )} />
              <FormField control={form.control} name="availability_weekends" render={({ field }) => (
                <FormItem className="flex items-center space-x-2 space-y-0">
                  <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  <FormLabel className="font-normal">Weekends</FormLabel>
                </FormItem>
              )} />
              {form.formState.errors.availability_weekdays && (
                <p className="text-sm font-medium text-destructive">
                  {form.formState.errors.availability_weekdays.message}
                </p>
              )}
            </div>

            <FormField control={form.control} name="minimum_notice" render={({ field }) => (
              <FormItem>
                <FormLabel>Minimum Notice Required</FormLabel>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-1">
                  {[
                    { value: "24h", label: "24 hours" },
                    { value: "48h", label: "48 hours" },
                    { value: "72h", label: "72 hours" },
                    { value: "1week", label: "1 week" },
                  ].map(opt => (
                    <FormItem key={opt.value} className="flex items-center space-x-2 space-y-0">
                      <FormControl><RadioGroupItem value={opt.value} /></FormControl>
                      <FormLabel className="font-normal">{opt.label}</FormLabel>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormItem>
            )} />
          </div>

          {/* Travel Radius */}
          <FormField control={form.control} name="travel_radius_miles" render={({ field }) => (
            <FormItem>
              <FormLabel>Travel Radius (miles) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={1}
                  max={5000}
                  placeholder="e.g. 50"
                  {...field}
                  value={field.value ?? ""}
                  onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )} />

          {/* Consolidated Gear & Tech Rider */}
          <div className="space-y-4 p-4 rounded-lg border bg-muted/20">
            <div className="space-y-1">
              <p className="text-sm font-semibold">Gear & Equipment *</p>
              <p className="text-xs text-muted-foreground">
                Owning professional gear is a strong professionalism signal. This also helps us prepare your tech rider for gigs.
              </p>
            </div>

            <FormField control={form.control} name="owns_equipment" render={({ field }) => (
              <FormItem>
                <FormLabel>Do you own professional-grade gear? *</FormLabel>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="space-y-1">
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl><RadioGroupItem value="yes" /></FormControl>
                    <FormLabel className="font-normal">Yes, I own my own equipment</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl><RadioGroupItem value="no" /></FormControl>
                    <FormLabel className="font-normal">No, I don't own equipment</FormLabel>
                  </FormItem>
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )} />

            {form.watch("owns_equipment") === "yes" && (
              <>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Select all gear you own:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {GEAR_ITEMS.map(item => (
                      <FormField
                        key={item}
                        control={form.control}
                        name="gear_owned"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item)}
                                onCheckedChange={(checked) => {
                                  const current = field.value || [];
                                  field.onChange(
                                    checked
                                      ? [...current, item]
                                      : current.filter((v: string) => v !== item)
                                  );
                                }}
                              />
                            </FormControl>
                            <FormLabel className="font-normal text-xs">{item}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <FormField control={form.control} name="gear_notes" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Additional Gear / Brand Details</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g. Fender Telecaster, Fender Blues Jr amp, Boss tuner pedal..."
                        className="min-h-[60px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </>
            )}

            <div className="border-t pt-4 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold">What Do You Need From Us? *</p>
                <p className="text-xs text-muted-foreground">
                  Tell us what you typically need provided at a gig so we can prepare your tech rider.
                </p>
              </div>

              <FormField control={form.control} name="venue_needs" render={({ field }) => (
                <FormItem>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      "No Need — I'm fully self-sufficient",
                      "Amplifier / Backline",
                      "Microphone",
                      "Stage Monitor / Wedge",
                      "Music Stand",
                      "Power / Extension Cord",
                    ].map(item => (
                      <FormItem key={item} className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(item)}
                            onCheckedChange={(checked) => {
                              const current = field.value || [];
                              if (item === "No Need — I'm fully self-sufficient") {
                                field.onChange(checked ? [item] : []);
                              } else {
                                const withoutNoNeed = current.filter((v: string) => v !== "No Need — I'm fully self-sufficient");
                                field.onChange(
                                  checked
                                    ? [...withoutNoNeed, item]
                                    : withoutNoNeed.filter((v: string) => v !== item)
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal text-xs">{item}</FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField control={form.control} name="tech_rider_notes" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Additional Tech Requirements</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g. I need a 1/4 inch input on the DI, or a specific mic for my instrument..."
                      className="min-h-[60px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
          </div>

          <Button type="submit" className="w-full" size="lg">
            Continue to Performance Tests →
          </Button>
        </form>
      </Form>
    </div>
  );
};
